<template>
  <main class="mobile-page service-screen">
    <div class="mobile-screen">
      <header class="mobile-topnav">
        <RouterLink class="mobile-icon-btn" to="/mobile/user" aria-label="返回我的">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M14.5 5.5 8 12l6.5 6.5" /></svg>
        </RouterLink>
        <span class="mobile-topnav-title">账号资料</span>
        <i class="nav-space"></i>
      </header>

      <div v-if="loading" class="mobile-card loading-card" aria-live="polite">正在加载账号资料...</div>
      <div v-else-if="message" class="mobile-card message-card" role="alert">
        <p>{{ message }}</p>
        <button class="mobile-btn mobile-btn-primary" type="button" @click="loadProfile">重新加载</button>
      </div>

      <template v-else>
        <section class="mobile-card profile-card">
          <i class="avatar">
            <img v-if="profile.avatar" :src="profile.avatar" alt="用户头像">
            <span v-else>{{ initial }}</span>
          </i>
          <div>
            <span class="mobile-eyebrow">Account · 账号信息</span>
            <h1>{{ displayName }}</h1>
            <p>{{ profile.phone || localPhone || '已登录账号' }}</p>
          </div>
        </section>

        <section class="mobile-card detail-card">
          <h2>基础资料</h2>
          <dl>
            <div><dt>昵称</dt><dd>{{ profile.nickname || '-' }}</dd></div>
            <div><dt>手机号</dt><dd>{{ profile.phone || localPhone || '-' }}</dd></div>
            <div><dt>我的邀请码</dt><dd>{{ profile.inviteCode || '-' }}</dd></div>
            <div><dt>注册时间</dt><dd>{{ formatDate(profile.registerTime) }}</dd></div>
            <div><dt>账号状态</dt><dd>{{ statusText(profile.status) }}</dd></div>
          </dl>
        </section>

        <section class="mobile-card detail-card">
          <h2>账号服务</h2>
          <dl>
            <div><dt>实名认证</dt><dd>{{ profile.identityAuthStatusText || identityText(profile.identityAuthStatus) }}</dd></div>
            <div><dt>最后活跃</dt><dd>{{ formatDate(profile.lastActiveTime) }}</dd></div>
          </dl>
        </section>
      </template>
    </div>
    <MobileTabbar active="user" />
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import MobileTabbar from '@/components/mobile/MobileTabbar.vue'
import { getCurrentUserInfo } from '@/api/user'

const router = useRouter()
const localPhone = ref(localStorage.getItem('dzzj_website_phone') || '')
const profile = ref({})
const loading = ref(false)
const message = ref('')
const displayName = computed(() => profile.value.nickname || localPhone.value.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2') || '官网用户')
const initial = computed(() => displayName.value.slice(0, 1).toUpperCase())
const region = computed(() => {
  const value = [profile.value.province, profile.value.city, profile.value.district].filter(Boolean).join(' ')
  return value || '-'
})

async function loadProfile() {
  if (!localStorage.getItem('dzzj_website_token')) {
    router.push({ path: '/mobile/login', query: { redirect: '/mobile/profile' } })
    return
  }
  loading.value = true
  message.value = ''
  try {
    profile.value = await getCurrentUserInfo()
  } catch (error) {
    message.value = error.message || '账号资料加载失败'
  } finally {
    loading.value = false
  }
}

function statusText(status) {
  return {
    0: '已禁用',
    1: '正常',
    2: '已注销'
  }[Number(status)] || '正常'
}

function identityText(status) {
  return {
    0: '未认证',
    1: '认证中',
    2: '已认证',
    3: '认证失败'
  }[Number(status)] || '未认证'
}

function formatDate(value) {
  return value ? String(value).replace('T', ' ').slice(0, 16) : '-'
}

onMounted(loadProfile)
</script>

<style scoped>
.service-screen { padding-bottom: calc(104px + env(safe-area-inset-bottom)); }
.nav-space { width: 36px; flex: 0 0 36px; }
.profile-card { display: flex; align-items: center; gap: 14px; margin-top: 22px; }
.avatar { width: 64px; height: 64px; flex: 0 0 64px; display: flex; align-items: center; justify-content: center; overflow: hidden; border-radius: 20px; color: var(--mobile-cta-ink); font-size: 22px; font-weight: 900; font-style: normal; background: radial-gradient(circle at 30% 25%, #8df5de, #46d6b6 60%, #2eb898); }
.avatar img { width: 100%; height: 100%; object-fit: cover; }
.profile-card h1 { margin: 10px 0 5px; font-size: 20px; font-weight: 900; line-height: 1.25; }
.profile-card p { color: var(--mobile-muted); font-size: 12px; font-variant-numeric: tabular-nums; }
.detail-card h2 { margin-bottom: 4px; font-size: 15px; font-weight: 900; }
.detail-card dl > div { display: flex; gap: 16px; padding: 11px 0; border-bottom: 1px dashed var(--mobile-line); }
.detail-card dl > div:last-child { border-bottom: none; }
.detail-card dt { flex: none; width: 96px; color: var(--mobile-quiet); font-size: 11.5px; font-weight: 700; }
.detail-card dd { flex: 1; min-width: 0; color: var(--mobile-ink); font-size: 12.5px; font-weight: 700; line-height: 1.6; word-break: break-all; }
.loading-card, .message-card { margin-top: 22px; text-align: center; }
.loading-card { color: var(--mobile-muted); font-size: 12.5px; }
.message-card { display: grid; gap: 12px; }
.message-card p { color: #ff9c9c; font-size: 12.5px; line-height: 1.6; }
@media (max-width: 350px) { .detail-card dt { width: 84px; } }
</style>
